import {
  ScenarioPreset,
  NodeSpecs,
  FormFactor,
  FormFactorDefinition,
  GpuSpecs,
  GpuVendor,
  GpuTier,
} from '../types/configurator';

// ============================================
// FORM FACTOR DEFINITIONS
// ============================================

export const FORM_FACTORS: FormFactorDefinition[] = [
  {
    id: 'rack-18',
    name: '18" Short-Depth Rack',
    description: 'Compact rack-mount for office/closet deployment. Recommended for most use cases.',
    recommended: true,
    hardwareFilters: {
      allowRackMount: true,
      allowDesktop: false,
      allowIndustrial: true,
      allowSBC: true,
      maxPowerWatts: 2000,
    },
  },
  {
    id: 'rack-42',
    name: 'Standard 42U Rack',
    description: 'Full-size datacenter rack for maximum capacity and expansion.',
    recommended: true,
    hardwareFilters: {
      allowRackMount: true,
      allowDesktop: false,
      allowIndustrial: true,
      allowSBC: true,
    },
  },
  {
    id: 'desktop',
    name: 'Desktop/Tower',
    description: 'Tower cases and desktop hardware. Good for small offices.',
    recommended: false,
    hardwareFilters: {
      allowRackMount: false,
      allowDesktop: true,
      allowIndustrial: false,
      allowSBC: true,
      maxPowerWatts: 1000,
    },
  },
  {
    id: 'container',
    name: 'Portable Container',
    description: 'Ruggedized for shipping containers or mobile deployment.',
    recommended: false,
    hardwareFilters: {
      allowRackMount: true,
      allowDesktop: false,
      allowIndustrial: true,
      allowSBC: true,
    },
  },
  {
    id: 'embedded',
    name: 'Embedded System',
    description: 'Integrated into equipment. SBCs and low-power only.',
    recommended: false,
    hardwareFilters: {
      allowRackMount: false,
      allowDesktop: false,
      allowIndustrial: true,
      allowSBC: true,
      maxPowerWatts: 200,
    },
  },
];

export function getFormFactorById(id: FormFactor): FormFactorDefinition | undefined {
  return FORM_FACTORS.find((f) => f.id === id);
}

// ============================================
// PRICING
// ============================================

export const PRICING_DISCLAIMER =
  'Prices are estimates only and may include a markup for integration and support. ' +
  'Actual costs vary based on supplier availability, shipping, and market conditions. ' +
  'Contact a vendor for accurate quotes.';

// ============================================
// GPU CATALOG
// ============================================

export const GPU_CATALOG: Record<string, GpuSpecs> = {
  // NVIDIA - Edge/Entry
  'nvidia-jetson-orin-nano': {
    vendor: 'nvidia',
    model: 'Jetson Orin Nano',
    vramGB: 8,
    tdpWatts: 15,
    tier: 'edge',
    computeCapability: '8.7',
    price: 750,
  },
  'nvidia-jetson-agx-orin': {
    vendor: 'nvidia',
    model: 'Jetson AGX Orin',
    vramGB: 32,
    tdpWatts: 60,
    tier: 'edge',
    computeCapability: '8.7',
    price: 2750,
  },
  // NVIDIA - Desktop
  'nvidia-rtx-4060': {
    vendor: 'nvidia',
    model: 'GeForce RTX 4060',
    vramGB: 8,
    tdpWatts: 115,
    tier: 'desktop',
    computeCapability: '8.9',
    price: 475,
  },
  'nvidia-rtx-4070': {
    vendor: 'nvidia',
    model: 'GeForce RTX 4070',
    vramGB: 12,
    tdpWatts: 200,
    tier: 'desktop',
    computeCapability: '8.9',
    price: 815,
  },
  'nvidia-rtx-4080': {
    vendor: 'nvidia',
    model: 'GeForce RTX 4080',
    vramGB: 16,
    tdpWatts: 320,
    tier: 'desktop',
    computeCapability: '8.9',
    price: 1625,
  },
  'nvidia-rtx-4090': {
    vendor: 'nvidia',
    model: 'GeForce RTX 4090',
    vramGB: 24,
    tdpWatts: 450,
    tier: 'desktop',
    computeCapability: '8.9',
    price: 2750,
  },
  // NVIDIA - Datacenter
  'nvidia-l4': {
    vendor: 'nvidia',
    model: 'L4 Tensor Core',
    vramGB: 24,
    tdpWatts: 72,
    tier: 'datacenter',
    computeCapability: '8.9',
    price: 6900,
  },
  'nvidia-l40': {
    vendor: 'nvidia',
    model: 'L40',
    vramGB: 48,
    tdpWatts: 300,
    tier: 'datacenter',
    computeCapability: '8.9',
    price: 17500,
  },
  'nvidia-a100-40gb': {
    vendor: 'nvidia',
    model: 'A100 40GB',
    vramGB: 40,
    tdpWatts: 400,
    tier: 'datacenter',
    computeCapability: '8.0',
    price: 22500,
  },
  'nvidia-h100': {
    vendor: 'nvidia',
    model: 'H100 PCIe',
    vramGB: 80,
    tdpWatts: 350,
    tier: 'datacenter',
    computeCapability: '9.0',
    price: 50000,
  },
  // NVIDIA - Workstation
  'nvidia-rtx-a6000': {
    vendor: 'nvidia',
    model: 'RTX A6000',
    vramGB: 48,
    tdpWatts: 300,
    tier: 'workstation',
    computeCapability: '8.6',
    price: 5500,
  },
  'nvidia-rtx-6000-ada': {
    vendor: 'nvidia',
    model: 'RTX 6000 Ada',
    vramGB: 48,
    tdpWatts: 300,
    tier: 'workstation',
    computeCapability: '8.9',
    price: 7500,
  },
  // NVIDIA - Blackwell (B-series)
  'nvidia-b100': {
    vendor: 'nvidia',
    model: 'B100',
    vramGB: 192,
    tdpWatts: 700,
    tier: 'datacenter',
    computeCapability: '10.0',
    price: 40000,
  },
  'nvidia-b200': {
    vendor: 'nvidia',
    model: 'B200',
    vramGB: 192,
    tdpWatts: 1000,
    tier: 'datacenter',
    computeCapability: '10.0',
    price: 55000,
  },
  'nvidia-gb200': {
    vendor: 'nvidia',
    model: 'GB200 NVL72',
    vramGB: 384,
    tdpWatts: 2700,
    tier: 'datacenter',
    computeCapability: '10.0',
    price: 150000,
  },
  // AMD - Desktop/Entry
  'amd-rx-7600': {
    vendor: 'amd',
    model: 'Radeon RX 7600',
    vramGB: 8,
    tdpWatts: 165,
    tier: 'entry',
    rocmSupport: true,
    price: 400,
  },
  'amd-rx-7800xt': {
    vendor: 'amd',
    model: 'Radeon RX 7800 XT',
    vramGB: 16,
    tdpWatts: 263,
    tier: 'desktop',
    rocmSupport: true,
    price: 725,
  },
  'amd-rx-7900xtx': {
    vendor: 'amd',
    model: 'Radeon RX 7900 XTX',
    vramGB: 24,
    tdpWatts: 355,
    tier: 'desktop',
    rocmSupport: true,
    price: 1500,
  },
  // AMD - RDNA 4 (9000 series)
  'amd-rx-9070': {
    vendor: 'amd',
    model: 'Radeon RX 9070',
    vramGB: 16,
    tdpWatts: 220,
    tier: 'desktop',
    rocmSupport: true,
    price: 549,
  },
  'amd-rx-9070xt': {
    vendor: 'amd',
    model: 'Radeon RX 9070 XT',
    vramGB: 16,
    tdpWatts: 290,
    tier: 'desktop',
    rocmSupport: true,
    price: 749,
  },
  // AMD - Workstation
  'amd-pro-w7800': {
    vendor: 'amd',
    model: 'Radeon PRO W7800',
    vramGB: 32,
    tdpWatts: 260,
    tier: 'workstation',
    rocmSupport: true,
    price: 3500,
  },
  'amd-pro-w7900': {
    vendor: 'amd',
    model: 'Radeon PRO W7900',
    vramGB: 48,
    tdpWatts: 295,
    tier: 'workstation',
    rocmSupport: true,
    price: 6000,
  },
  // AMD - Datacenter
  'amd-mi210': {
    vendor: 'amd',
    model: 'Instinct MI210',
    vramGB: 64,
    tdpWatts: 300,
    tier: 'datacenter',
    rocmSupport: true,
    price: 17500,
  },
  'amd-mi300x': {
    vendor: 'amd',
    model: 'Instinct MI300X',
    vramGB: 192,
    tdpWatts: 750,
    tier: 'datacenter',
    rocmSupport: true,
    price: 27500,
  },
  // Intel Arc - Entry
  'intel-arc-a380': {
    vendor: 'intel',
    model: 'Intel Arc A380',
    vramGB: 6,
    tdpWatts: 75,
    tier: 'entry',
    price: 139,
  },
  // Intel Arc - Desktop
  'intel-arc-a750': {
    vendor: 'intel',
    model: 'Intel Arc A750',
    vramGB: 8,
    tdpWatts: 225,
    tier: 'desktop',
    price: 289,
  },
  'intel-arc-a770': {
    vendor: 'intel',
    model: 'Intel Arc A770',
    vramGB: 16,
    tdpWatts: 225,
    tier: 'desktop',
    price: 349,
  },
  // Intel Arc - Workstation/Pro
  'intel-arc-pro-a40': {
    vendor: 'intel',
    model: 'Intel Arc Pro A40',
    vramGB: 6,
    tdpWatts: 75,
    tier: 'workstation',
    price: 549,
  },
  'intel-arc-pro-a60': {
    vendor: 'intel',
    model: 'Intel Arc Pro A60',
    vramGB: 12,
    tdpWatts: 300,
    tier: 'workstation',
    price: 899,
  },
};

export type GpuModel = keyof typeof GPU_CATALOG;

export function getGpusByVendor(vendor: GpuVendor): GpuSpecs[] {
  return Object.values(GPU_CATALOG).filter((g) => g.vendor === vendor);
}

export function getGpusByTier(tier: GpuTier): GpuSpecs[] {
  return Object.values(GPU_CATALOG).filter((g) => g.tier === tier);
}

export function getGpusForFormFactor(formFactor: FormFactor): GpuSpecs[] {
  const ff = getFormFactorById(formFactor);
  if (!ff) return Object.values(GPU_CATALOG);

  return Object.values(GPU_CATALOG).filter((gpu) => {
    // Filter by power if form factor has limit
    if (ff.hardwareFilters.maxPowerWatts && gpu.tdpWatts > ff.hardwareFilters.maxPowerWatts) {
      return false;
    }
    // Desktop form factor: prefer desktop/entry tier
    if (formFactor === 'desktop') {
      return ['entry', 'desktop', 'edge'].includes(gpu.tier);
    }
    // Embedded: only edge tier
    if (formFactor === 'embedded') {
      return gpu.tier === 'edge';
    }
    return true;
  });
}

// ============================================
// BASE NODE SPECS
// ============================================

const RASPBERRY_PI_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 4,
  storageGB: 64,
  powerWatts: 5,
  model: 'Raspberry Pi 4B',
  storageType: 'emmc',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'embedded'],
  rackUnits: 1,
  depthMm: 100,
  gpuSlots: 0,  // SBC - no discrete GPU support
};

const PI_COMPUTE_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 8,
  storageGB: 128,
  powerWatts: 8,
  model: 'Raspberry Pi CM4',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'embedded'],
  rackUnits: 1,
  depthMm: 100,
  gpuSlots: 0,  // SBC - no discrete GPU support
};

const MINI_PC_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 16,
  storageGB: 512,
  powerWatts: 35,
  model: 'Intel N100 Mini PC',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 1,
  depthMm: 150,
  gpuSlots: 0,  // Mini PC - no discrete GPU slot
};

const STORAGE_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 8,
  storageGB: 4000,
  powerWatts: 25,
  model: 'ODROID HC4',
  storageType: 'hdd',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 1,
  depthMm: 200,
  gpuSlots: 0,  // Storage appliance - no GPU
};

const GPU_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 1000,
  powerWatts: 65,
  model: 'Nvidia Jetson AGX',
  storageType: 'nvme',
  gpu: GPU_CATALOG['nvidia-jetson-agx-orin'],
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'embedded'],
  rackUnits: 1,
  depthMm: 150,
  gpuSlots: 0,  // Jetson has integrated GPU, no discrete GPU slots
};

const DAQ_SPECS: NodeSpecs = {
  cpuCores: 2,
  memoryGB: 2,
  storageGB: 32,
  powerWatts: 10,
  model: 'BeagleBone Black',
  storageType: 'emmc',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'container', 'embedded'],
  rackUnits: 1,
  depthMm: 90,
  gpuSlots: 0,  // SBC - no discrete GPU support
};

const INDUSTRIAL_PC_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 8,
  storageGB: 256,
  powerWatts: 20,
  model: 'Siemens IPC',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-18', 'rack-42', 'container'],
  rackUnits: 2,
  depthMm: 250,
  gpuSlots: 0,  // Industrial PC - no discrete GPU
};

const NUC_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 1000,
  powerWatts: 45,
  model: 'Intel NUC 13 Pro',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 1,
  depthMm: 120,
  gpuSlots: 0,  // NUC - no internal GPU slot
};

const PROXMOX_SERVER_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 64,
  storageGB: 2000,
  powerWatts: 120,
  model: 'Mini Server (Proxmox)',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 2,
  depthMm: 400,
  gpuSlots: 1,  // 2U server with 1 GPU slot
};

const NAS_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 16,
  storageGB: 32000,
  powerWatts: 60,
  model: 'Synology/TrueNAS',
  storageType: 'nas',
  storageVendor: 'Synology',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 2,
  depthMm: 300,
  gpuSlots: 0,  // NAS appliance - no GPU support
};

// ============================================
// NEW HARDWARE - Compute Options
// ============================================

const INTEL_N305_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 512,
  powerWatts: 15,
  model: 'Intel N305 Edge PC',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'embedded'],
  rackUnits: 1,
  depthMm: 120,
  gpuSlots: 0,  // Edge PC - no discrete GPU
};

const AMD_RYZEN_7_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 1000,
  powerWatts: 35,
  model: 'AMD Ryzen 7 7735HS',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 1,
  depthMm: 200,
  gpuSlots: 1,  // Desktop form factor with GPU slot
};

const XEON_W1390_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 128,
  storageGB: 2000,
  powerWatts: 80,
  model: 'Intel Xeon W-1390',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 2,
  depthMm: 450,
  gpuSlots: 1,  // Workstation with GPU slot
};

const EPYC_7313P_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 256,
  storageGB: 4000,
  powerWatts: 155,
  model: 'AMD EPYC 7313P',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 650,
  gpuSlots: 2,  // Server with dual GPU support
};

// ============================================
// NEW HARDWARE - Storage Options
// ============================================

const SYNOLOGY_DS423_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 16,
  storageGB: 72000,
  powerWatts: 40,
  model: 'Synology DS423+',
  storageType: 'nas',
  storageVendor: 'Synology',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 2,
  depthMm: 230,
  gpuSlots: 0,  // NAS appliance - no GPU
};

const QNAP_TS464_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 16,
  storageGB: 72000,
  powerWatts: 35,
  model: 'QNAP TS-464',
  storageType: 'nas',
  storageVendor: 'QNAP',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 2,
  depthMm: 235,
  gpuSlots: 0,  // NAS appliance - no GPU
};

const TRUENAS_MINI_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 80000,
  powerWatts: 75,
  model: 'TrueNAS Mini X+',
  storageType: 'nas',
  storageVendor: 'TrueNAS',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 3,
  depthMm: 350,
  gpuSlots: 0,  // NAS appliance - no GPU
};

// ============================================
// NEW HARDWARE - GPU Workstation Nodes
// ============================================

const GPU_WORKSTATION_RTX4070_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 2000,
  powerWatts: 85,
  model: 'GPU Workstation (RTX 4070)',
  storageType: 'nvme',
  gpu: GPU_CATALOG['nvidia-rtx-4070'],
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 2,
  depthMm: 350,
  gpuSlots: 1,  // Has GPU slot (pre-configured with RTX 4070)
};

const GPU_WORKSTATION_RTX4090_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 128,
  storageGB: 4000,
  powerWatts: 150,
  model: 'GPU Workstation (RTX 4090)',
  storageType: 'nvme',
  gpu: GPU_CATALOG['nvidia-rtx-4090'],
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 3,
  depthMm: 400,
  gpuSlots: 2,  // Has 2 GPU slots (pre-configured with RTX 4090)
};

const GPU_SERVER_L4_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 128,
  storageGB: 4000,
  powerWatts: 200,
  model: 'GPU Server (L4)',
  storageType: 'nvme',
  gpu: GPU_CATALOG['nvidia-l4'],
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 2,
  depthMm: 500,
  gpuSlots: 2,  // Server with 2 GPU slots
};

const GPU_SERVER_L40_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 256,
  storageGB: 8000,
  powerWatts: 350,
  model: 'GPU Server (L40)',
  storageType: 'nvme',
  gpu: GPU_CATALOG['nvidia-l40'],
  formFactorCompatibility: ['rack-42'],
  rackUnits: 4,
  depthMm: 750,
  gpuSlots: 4,  // 4U server with 4 GPU slots
};

const GPU_AMD_W7800_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 128,
  storageGB: 4000,
  powerWatts: 180,
  model: 'AMD Workstation (PRO W7800)',
  storageType: 'nvme',
  gpu: GPU_CATALOG['amd-pro-w7800'],
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 2,
  depthMm: 350,
  gpuSlots: 1,
};

// ============================================
// HP PROLIANT SERVERS
// ============================================

const HP_DL360_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 128,
  storageGB: 3200,
  powerWatts: 500,
  model: 'HP ProLiant DL360 Gen10',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 1,
  depthMm: 700,
  gpuSlots: 0,  // 1U, no space for discrete GPU
};

const HP_DL380_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 256,
  storageGB: 6400,
  powerWatts: 800,
  model: 'HP ProLiant DL380 Gen10',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 750,
  gpuSlots: 2,
};

const HP_DL380_GPU_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 512,
  storageGB: 6400,
  powerWatts: 1200,
  model: 'HP ProLiant DL380 Gen10 Plus (GPU)',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 750,
  gpuSlots: 3,
};

const HP_ML350_SPECS: NodeSpecs = {
  cpuCores: 24,
  memoryGB: 192,
  storageGB: 8000,
  powerWatts: 750,
  model: 'HP ProLiant ML350 Gen10',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 4,
  depthMm: 650,
  gpuSlots: 2,
};

// ============================================
// DELL POWEREDGE SERVERS
// ============================================

const DELL_R350_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 2400,
  powerWatts: 350,
  model: 'Dell PowerEdge R350',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 1,
  depthMm: 600,
  gpuSlots: 0,  // 1U entry server, no GPU
};

const DELL_R650_SPECS: NodeSpecs = {
  cpuCores: 24,
  memoryGB: 256,
  storageGB: 4800,
  powerWatts: 600,
  model: 'Dell PowerEdge R650',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 1,
  depthMm: 720,
  gpuSlots: 0,  // 1U, no full-height GPU
};

const DELL_R750_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 512,
  storageGB: 9600,
  powerWatts: 800,
  model: 'Dell PowerEdge R750',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 720,
  gpuSlots: 2,
};

const DELL_R750XA_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 512,
  storageGB: 6400,
  powerWatts: 1400,
  model: 'Dell PowerEdge R750xa (GPU)',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 720,
  gpuSlots: 4,  // GPU-optimized, up to 4 GPUs
};

const DELL_R760_SPECS: NodeSpecs = {
  cpuCores: 48,
  memoryGB: 1024,
  storageGB: 12800,
  powerWatts: 900,
  model: 'Dell PowerEdge R760',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 720,
  gpuSlots: 2,
};

const DELL_R760XA_SPECS: NodeSpecs = {
  cpuCores: 48,
  memoryGB: 1024,
  storageGB: 9600,
  powerWatts: 1600,
  model: 'Dell PowerEdge R760xa (GPU)',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 720,
  gpuSlots: 4,  // Latest gen GPU-optimized
};

const DELL_T350_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 4800,
  powerWatts: 400,
  model: 'Dell PowerEdge T350',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 4,
  depthMm: 550,
  gpuSlots: 1,  // Tower with GPU slot
};

const DELL_T550_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 256,
  storageGB: 9600,
  powerWatts: 700,
  model: 'Dell PowerEdge T550',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 5,
  depthMm: 600,
  gpuSlots: 2,  // Tower with 2 GPU slots
};

// ============================================
// SUPERMICRO SERVERS
// ============================================

const SUPERMICRO_E100_SPECS: NodeSpecs = {
  cpuCores: 4,
  memoryGB: 32,
  storageGB: 512,
  powerWatts: 35,
  model: 'Supermicro E100-12T',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'embedded'],
  rackUnits: 1,
  depthMm: 200,
  gpuSlots: 0,  // Compact edge server
};

const SUPERMICRO_E300_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 1000,
  powerWatts: 65,
  model: 'Supermicro E300-9D',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'embedded'],
  rackUnits: 1,
  depthMm: 230,
  gpuSlots: 0,  // Compact Xeon D server
};

const SUPERMICRO_5019S_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 2400,
  powerWatts: 200,
  model: 'Supermicro 5019S-MN4',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 1,
  depthMm: 430,
  gpuSlots: 0,  // 1U, no GPU
};

const SUPERMICRO_5019P_SPECS: NodeSpecs = {
  cpuCores: 16,
  memoryGB: 256,
  storageGB: 3200,
  powerWatts: 350,
  model: 'Supermicro 5019P-MTR',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 1,
  depthMm: 504,
  gpuSlots: 0,  // 1U Xeon Scalable
};

const SUPERMICRO_6029P_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 512,
  storageGB: 6400,
  powerWatts: 600,
  model: 'Supermicro 6029P-TRT',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 650,
  gpuSlots: 2,  // 2U dual Xeon
};

const SUPERMICRO_4029GP_SPECS: NodeSpecs = {
  cpuCores: 48,
  memoryGB: 1024,
  storageGB: 9600,
  powerWatts: 1500,
  model: 'Supermicro 4029GP-TRT',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 4,
  depthMm: 737,
  gpuSlots: 8,  // GPU SuperServer
};

const SUPERMICRO_2029U_SPECS: NodeSpecs = {
  cpuCores: 56,
  memoryGB: 2048,
  storageGB: 12800,
  powerWatts: 800,
  model: 'Supermicro 2029U-TN24R4T',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 730,
  gpuSlots: 2,  // Ultra high-density storage
};

const SUPERMICRO_WORKSTATION_SPECS: NodeSpecs = {
  cpuCores: 24,
  memoryGB: 256,
  storageGB: 4000,
  powerWatts: 450,
  model: 'Supermicro SuperWorkstation 7049A',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 4,
  depthMm: 600,
  gpuSlots: 4,  // Tower workstation
};

// ============================================
// LENOVO THINKSYSTEM SERVERS
// ============================================

const LENOVO_SE350_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 512,
  powerWatts: 45,
  model: 'Lenovo ThinkSystem SE350',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42', 'embedded'],
  rackUnits: 1,
  depthMm: 190,
  gpuSlots: 0,  // Edge server
};

const LENOVO_SR250_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 2400,
  powerWatts: 300,
  model: 'Lenovo ThinkSystem SR250 V2',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 1,
  depthMm: 470,
  gpuSlots: 0,  // 1U entry
};

const LENOVO_SR630_SPECS: NodeSpecs = {
  cpuCores: 24,
  memoryGB: 256,
  storageGB: 4800,
  powerWatts: 500,
  model: 'Lenovo ThinkSystem SR630 V3',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 1,
  depthMm: 735,
  gpuSlots: 0,  // 1U mainstream
};

const LENOVO_SR650_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 512,
  storageGB: 9600,
  powerWatts: 750,
  model: 'Lenovo ThinkSystem SR650 V3',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 735,
  gpuSlots: 2,  // 2U mainstream
};

const LENOVO_SR670_SPECS: NodeSpecs = {
  cpuCores: 48,
  memoryGB: 1024,
  storageGB: 6400,
  powerWatts: 1400,
  model: 'Lenovo ThinkSystem SR670 V2',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 788,
  gpuSlots: 4,  // GPU-optimized
};

const LENOVO_SR850_SPECS: NodeSpecs = {
  cpuCores: 112,
  memoryGB: 2048,
  storageGB: 12800,
  powerWatts: 1200,
  model: 'Lenovo ThinkSystem SR850 V3',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 915,
  gpuSlots: 4,  // 4-socket mission critical
};

const LENOVO_SD530_SPECS: NodeSpecs = {
  cpuCores: 28,
  memoryGB: 256,
  storageGB: 3200,
  powerWatts: 400,
  model: 'Lenovo ThinkSystem SD530',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 1,  // Half-width, 2 per 1U
  depthMm: 575,
  gpuSlots: 0,  // High-density compute
};

const LENOVO_ST250_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 4800,
  powerWatts: 350,
  model: 'Lenovo ThinkSystem ST250 V2',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 4,
  depthMm: 440,
  gpuSlots: 1,  // Tower entry
};

const LENOVO_ST650_SPECS: NodeSpecs = {
  cpuCores: 32,
  memoryGB: 256,
  storageGB: 9600,
  powerWatts: 600,
  model: 'Lenovo ThinkSystem ST650 V2',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42', 'desktop'],
  rackUnits: 5,
  depthMm: 540,
  gpuSlots: 2,  // Tower mainstream
};

// ============================================
// ARM64 SERVER SPECS
// ============================================

// Ampere Altra (cloud-native ARM64)
const AMPERE_ALTRA_MT_SPECS: NodeSpecs = {
  cpuCores: 80,
  memoryGB: 512,
  storageGB: 7680,
  powerWatts: 250,
  model: 'Ampere Altra Mt. Jade',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 710,
  gpuSlots: 0,  // Cloud-native, no GPU
  manufacturer: 'Ampere',
  description: '80-core ARM64 for cloud-native workloads',
};

const AMPERE_ALTRA_MAX_SPECS: NodeSpecs = {
  cpuCores: 128,
  memoryGB: 1024,
  storageGB: 15360,
  powerWatts: 350,
  model: 'Ampere Altra Max',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 710,
  gpuSlots: 0,
  manufacturer: 'Ampere',
  description: '128-core ARM64 highest density',
};

// NVIDIA Grace (ARM64 + GPU)
const NVIDIA_GRACE_HOPPER_SPECS: NodeSpecs = {
  cpuCores: 72,
  memoryGB: 480,  // LPDDR5X unified memory
  storageGB: 3840,
  powerWatts: 900,  // GH200 TDP
  model: 'NVIDIA Grace Hopper GH200',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 4,
  depthMm: 800,
  gpuSlots: 0,  // Integrated H100 GPU
  manufacturer: 'NVIDIA',
  description: 'Grace ARM64 CPU + Hopper H100 GPU superchip',
};

const NVIDIA_GRACE_CPU_SPECS: NodeSpecs = {
  cpuCores: 144,  // Dual Grace
  memoryGB: 960,
  storageGB: 7680,
  powerWatts: 500,
  model: 'NVIDIA Grace CPU Superchip',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 2,
  depthMm: 780,
  gpuSlots: 0,
  manufacturer: 'NVIDIA',
  description: 'Dual Grace ARM64 CPUs, 144 cores total',
};

// Marvell ThunderX3 (edge/telco ARM64)
const MARVELL_THUNDERX3_SPECS: NodeSpecs = {
  cpuCores: 96,
  memoryGB: 256,
  storageGB: 3840,
  powerWatts: 180,
  model: 'Marvell ThunderX3',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'rack-42'],
  rackUnits: 1,
  depthMm: 600,
  gpuSlots: 0,
  manufacturer: 'Marvell',
  description: '96-core ARM64 for edge and telco',
};

// ADLINK/Kontron ARM64 Edge
const ADLINK_COM_EXPRESS_ARM_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 512,
  powerWatts: 45,
  model: 'ADLINK COM-HPC ARM',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'embedded'],
  rackUnits: 1,
  depthMm: 300,
  gpuSlots: 0,
  manufacturer: 'ADLINK',
  description: 'ARM64 COM-HPC module for edge',
};

// Rockchip RK3588 Edge Server
const ROCKCHIP_RK3588_SERVER_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 32,
  storageGB: 256,
  powerWatts: 25,
  model: 'Rockchip RK3588 Edge Server',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-18', 'desktop', 'embedded'],
  rackUnits: 1,
  depthMm: 250,
  gpuSlots: 0,
  manufacturer: 'Various',
  description: 'Quad A76 + Quad A55 edge compute',
};

// Phytium D2000 (Chinese ARM64)
const PHYTIUM_D2000_SPECS: NodeSpecs = {
  cpuCores: 8,
  memoryGB: 64,
  storageGB: 1024,
  powerWatts: 70,
  model: 'Phytium D2000',
  storageType: 'ssd',
  formFactorCompatibility: ['rack-18', 'rack-42', 'desktop'],
  rackUnits: 1,
  depthMm: 400,
  gpuSlots: 1,
  manufacturer: 'Phytium',
  description: 'ARM64 desktop/server processor',
};

// ============================================
// NVIDIA DGX SPECS
// ============================================

const NVIDIA_DGX_A100_SPECS: NodeSpecs = {
  cpuCores: 128,  // Dual AMD EPYC 7742
  memoryGB: 2048,
  storageGB: 30720,  // 30TB NVMe
  powerWatts: 6500,
  model: 'NVIDIA DGX A100',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 6,
  depthMm: 900,
  gpuSlots: 8,  // 8x A100-80GB
  manufacturer: 'NVIDIA',
  description: '8x A100-80GB, 640GB HBM2e total',
};

const NVIDIA_DGX_H100_SPECS: NodeSpecs = {
  cpuCores: 112,  // Dual Intel Xeon Platinum
  memoryGB: 2048,
  storageGB: 30720,  // 30TB NVMe
  powerWatts: 10200,
  model: 'NVIDIA DGX H100',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 8,
  depthMm: 1000,
  gpuSlots: 8,  // 8x H100-80GB
  manufacturer: 'NVIDIA',
  description: '8x H100-80GB SXM5, 640GB HBM3 total',
};

const NVIDIA_DGX_STATION_A100_SPECS: NodeSpecs = {
  cpuCores: 64,  // AMD EPYC 7742
  memoryGB: 512,
  storageGB: 7680,  // 7.68TB NVMe
  powerWatts: 1500,
  model: 'NVIDIA DGX Station A100',
  storageType: 'nvme',
  formFactorCompatibility: ['desktop'],
  rackUnits: 0,  // Desktop form factor
  depthMm: 640,
  gpuSlots: 4,  // 4x A100-80GB
  manufacturer: 'NVIDIA',
  description: 'Desktop workstation, 4x A100-80GB',
};

const NVIDIA_DGX_BASEPOD_SPECS: NodeSpecs = {
  cpuCores: 896,  // 7 DGX H100 systems (112 cores each)
  memoryGB: 14336,  // 7x 2TB
  storageGB: 215040,  // 210TB NVMe
  powerWatts: 75000,  // Approximate for full pod
  model: 'NVIDIA DGX BasePOD',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 42,  // Full rack
  depthMm: 1200,
  gpuSlots: 56,  // 7x 8 H100 GPUs
  manufacturer: 'NVIDIA',
  description: 'Pre-integrated AI infrastructure, 56x H100',
};

const NVIDIA_DGX_SUPERPOD_SPECS: NodeSpecs = {
  cpuCores: 3584,  // 32 DGX H100 systems
  memoryGB: 65536,  // 64TB
  storageGB: 983040,  // ~1PB
  powerWatts: 350000,  // Full SuperPOD
  model: 'NVIDIA DGX SuperPOD',
  storageType: 'nvme',
  formFactorCompatibility: ['rack-42'],
  rackUnits: 42,  // Multiple racks
  depthMm: 1200,
  gpuSlots: 256,  // 32x 8 H100 GPUs
  manufacturer: 'NVIDIA',
  description: 'Enterprise AI supercomputer, 256x H100',
};

// ============================================
// HARDWARE CATALOG
// ============================================

export const HARDWARE_CATALOG = {
  // Entry/Edge
  'raspberry-pi-4b': { ...RASPBERRY_PI_SPECS, price: 120 },
  'raspberry-pi-cm4': { ...PI_COMPUTE_SPECS, price: 175 },
  'beaglebone': { ...DAQ_SPECS, price: 100 },

  // Mini PCs
  'intel-n100': { ...MINI_PC_SPECS, price: 350 },
  'intel-n305': { ...INTEL_N305_SPECS, price: 475 },
  'intel-nuc-13': { ...NUC_SPECS, price: 1125 },

  // Workstations
  'amd-ryzen-7': { ...AMD_RYZEN_7_SPECS, price: 1750 },
  'xeon-w1390': { ...XEON_W1390_SPECS, price: 3000 },
  'epyc-7313p': { ...EPYC_7313P_SPECS, price: 6000 },

  // Servers
  'siemens-ipc': { ...INDUSTRIAL_PC_SPECS, price: 950 },
  'proxmox-server': { ...PROXMOX_SERVER_SPECS, price: 2750 },

  // Storage
  'odroid-hc4': { ...STORAGE_SPECS, price: 165 },
  'synology-ds423': { ...SYNOLOGY_DS423_SPECS, price: 1200 },
  'qnap-ts464': { ...QNAP_TS464_SPECS, price: 1100 },
  'truenas-mini': { ...TRUENAS_MINI_SPECS, price: 3000 },
  'nas-server': { ...NAS_SPECS, price: 1500 },

  // GPU Nodes (full system price including GPU)
  'jetson-orin-nano': {
    cpuCores: 6,
    memoryGB: 8,
    storageGB: 256,
    powerWatts: 15,
    model: 'Jetson Orin Nano',
    storageType: 'nvme' as const,
    gpu: GPU_CATALOG['nvidia-jetson-orin-nano'],
    formFactorCompatibility: ['rack-18', 'rack-42', 'desktop', 'embedded'] as FormFactor[],
    rackUnits: 1,
    depthMm: 100,
    gpuSlots: 0,  // Jetson has integrated GPU
    price: 1000,
  },
  'jetson-agx': { ...GPU_SPECS, price: 3500 },
  'gpu-rtx4070': { ...GPU_WORKSTATION_RTX4070_SPECS, price: 3000 },
  'gpu-rtx4090': { ...GPU_WORKSTATION_RTX4090_SPECS, price: 6500 },
  'gpu-l4': { ...GPU_SERVER_L4_SPECS, price: 12000 },
  'gpu-l40': { ...GPU_SERVER_L40_SPECS, price: 24000 },
  'gpu-amd-w7800': { ...GPU_AMD_W7800_SPECS, price: 7800 },

  // HP ProLiant Servers
  'hp-dl360-gen10': { ...HP_DL360_SPECS, price: 4500 },
  'hp-dl380-gen10': { ...HP_DL380_SPECS, price: 6500 },
  'hp-dl380-gen10-gpu': { ...HP_DL380_GPU_SPECS, price: 9500 },
  'hp-ml350-gen10': { ...HP_ML350_SPECS, price: 5500 },

  // Dell PowerEdge Servers
  'dell-r350': { ...DELL_R350_SPECS, price: 2800 },
  'dell-r650': { ...DELL_R650_SPECS, price: 5500 },
  'dell-r750': { ...DELL_R750_SPECS, price: 8500 },
  'dell-r750xa': { ...DELL_R750XA_SPECS, price: 14000 },
  'dell-r760': { ...DELL_R760_SPECS, price: 12000 },
  'dell-r760xa': { ...DELL_R760XA_SPECS, price: 18000 },
  'dell-t350': { ...DELL_T350_SPECS, price: 3200 },
  'dell-t550': { ...DELL_T550_SPECS, price: 7500 },

  // Supermicro Servers
  'supermicro-e100': { ...SUPERMICRO_E100_SPECS, price: 800 },
  'supermicro-e300': { ...SUPERMICRO_E300_SPECS, price: 1400 },
  'supermicro-5019s': { ...SUPERMICRO_5019S_SPECS, price: 2200 },
  'supermicro-5019p': { ...SUPERMICRO_5019P_SPECS, price: 4500 },
  'supermicro-6029p': { ...SUPERMICRO_6029P_SPECS, price: 7500 },
  'supermicro-4029gp': { ...SUPERMICRO_4029GP_SPECS, price: 18000 },
  'supermicro-2029u': { ...SUPERMICRO_2029U_SPECS, price: 12000 },
  'supermicro-workstation': { ...SUPERMICRO_WORKSTATION_SPECS, price: 6500 },

  // Lenovo ThinkSystem Servers
  'lenovo-se350': { ...LENOVO_SE350_SPECS, price: 2800 },
  'lenovo-sr250': { ...LENOVO_SR250_SPECS, price: 2400 },
  'lenovo-sr630': { ...LENOVO_SR630_SPECS, price: 5500 },
  'lenovo-sr650': { ...LENOVO_SR650_SPECS, price: 8500 },
  'lenovo-sr670': { ...LENOVO_SR670_SPECS, price: 16000 },
  'lenovo-sr850': { ...LENOVO_SR850_SPECS, price: 25000 },
  'lenovo-sd530': { ...LENOVO_SD530_SPECS, price: 4800 },
  'lenovo-st250': { ...LENOVO_ST250_SPECS, price: 3000 },
  'lenovo-st650': { ...LENOVO_ST650_SPECS, price: 7000 },

  // ARM64 Servers
  'ampere-altra-mt': { ...AMPERE_ALTRA_MT_SPECS, price: 8500 },
  'ampere-altra-max': { ...AMPERE_ALTRA_MAX_SPECS, price: 15000 },
  'nvidia-grace-hopper': { ...NVIDIA_GRACE_HOPPER_SPECS, price: 35000 },
  'nvidia-grace-cpu': { ...NVIDIA_GRACE_CPU_SPECS, price: 18000 },
  'marvell-thunderx3': { ...MARVELL_THUNDERX3_SPECS, price: 6000 },
  'adlink-arm-edge': { ...ADLINK_COM_EXPRESS_ARM_SPECS, price: 1800 },
  'rockchip-rk3588': { ...ROCKCHIP_RK3588_SERVER_SPECS, price: 400 },
  'phytium-d2000': { ...PHYTIUM_D2000_SPECS, price: 1200 },

  // NVIDIA DGX Systems
  'nvidia-dgx-a100': { ...NVIDIA_DGX_A100_SPECS, price: 199000 },
  'nvidia-dgx-h100': { ...NVIDIA_DGX_H100_SPECS, price: 300000 },
  'nvidia-dgx-station': { ...NVIDIA_DGX_STATION_A100_SPECS, price: 149000 },
  'nvidia-dgx-basepod': { ...NVIDIA_DGX_BASEPOD_SPECS, price: 2500000 },
  'nvidia-dgx-superpod': { ...NVIDIA_DGX_SUPERPOD_SPECS, price: 12000000 },
} as const;

export type HardwareModel = keyof typeof HARDWARE_CATALOG;

export function getHardwareForFormFactor(formFactor: FormFactor): (typeof HARDWARE_CATALOG)[HardwareModel][] {
  return Object.values(HARDWARE_CATALOG).filter((hw) => {
    if (!hw.formFactorCompatibility) return true;
    return hw.formFactorCompatibility.includes(formFactor);
  });
}

// ============================================
// SCENARIO PRESETS
// ============================================

export const SCENARIO_PRESETS: ScenarioPreset[] = [
  // Original scenarios
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Proof-of-concept with 2 nodes. Ideal for development and testing.',
    nodeCount: 2,
    estimatedCost: 250,
    estimatedPower: 30,
    useCases: [
      'Development environment',
      'Proof of concept',
      'Learning/training',
      'Single workload testing',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: RASPBERRY_PI_SPECS },
      { type: 'compute', count: 1, specs: RASPBERRY_PI_SPECS, role: 'hybrid' },
    ],
  },
  {
    id: 'lab-bench',
    name: 'Lab Bench',
    description: 'Test lab configuration with 4 nodes and DAQ support.',
    nodeCount: 4,
    estimatedCost: 600,
    estimatedPower: 80,
    useCases: [
      'Test laboratory',
      'Research experiments',
      'Data acquisition',
      'Multi-service deployment',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: RASPBERRY_PI_SPECS },
      { type: 'compute', count: 2, specs: PI_COMPUTE_SPECS, role: 'worker' },
      { type: 'daq', count: 1, specs: DAQ_SPECS },
    ],
  },
  {
    id: 'research',
    name: 'Research Cluster',
    description: 'Full compute cluster with 6 nodes for ML and simulation.',
    nodeCount: 6,
    estimatedCost: 5600,
    estimatedPower: 150,
    useCases: [
      'Machine learning training',
      'Scientific simulation',
      'High-performance computing',
      'Large dataset processing',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: MINI_PC_SPECS },
      { type: 'compute', count: 3, specs: MINI_PC_SPECS, role: 'worker' },
      { type: 'compute', count: 1, specs: GPU_SPECS, role: 'worker' },
      { type: 'storage', count: 1, specs: STORAGE_SPECS },
    ],
  },
  {
    id: 'industrial',
    name: 'Industrial OT',
    description: 'Factory/SCADA environment with 5 nodes and industrial-grade components.',
    nodeCount: 5,
    estimatedCost: 3400,
    estimatedPower: 120,
    useCases: [
      'Factory automation',
      'SCADA systems',
      'Industrial IoT',
      'Process control',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: INDUSTRIAL_PC_SPECS },
      { type: 'compute', count: 2, specs: INDUSTRIAL_PC_SPECS, role: 'worker' },
      { type: 'storage', count: 1, specs: STORAGE_SPECS },
      { type: 'daq', count: 1, specs: DAQ_SPECS },
    ],
  },
  // NEW: GDPR Isolated LLM
  {
    id: 'gdpr-llm',
    name: 'GDPR Isolated LLM',
    description: 'Local LLM inference for schools, medical offices, and legal firms. Data never leaves premises.',
    nodeCount: 4,
    estimatedCost: 7600,
    estimatedPower: 350,
    useCases: [
      'School AI assistant',
      'Medical documentation',
      'Legal document analysis',
      'GDPR-compliant AI',
      'Private ChatGPT alternative',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: MINI_PC_SPECS },
      { type: 'compute', count: 1, specs: GPU_WORKSTATION_RTX4070_SPECS, role: 'worker' },
      { type: 'compute', count: 1, specs: GPU_WORKSTATION_RTX4070_SPECS, role: 'worker' },
      { type: 'storage', count: 1, specs: SYNOLOGY_DS423_SPECS },
    ],
  },
  // NEW: AI Rendering Studio
  {
    id: 'ai-rendering',
    name: 'AI Rendering Studio',
    description: 'GPU cluster for AI image generation, Stable Diffusion, and video processing.',
    nodeCount: 5,
    estimatedCost: 20500,
    estimatedPower: 800,
    useCases: [
      'Stable Diffusion / SDXL',
      'AI video generation',
      'Content creator team',
      'Batch rendering',
      'LoRA training',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: NUC_SPECS },
      { type: 'compute', count: 2, specs: GPU_WORKSTATION_RTX4090_SPECS, role: 'worker' },
      { type: 'compute', count: 1, specs: GPU_WORKSTATION_RTX4070_SPECS, role: 'worker' },
      { type: 'storage', count: 1, specs: TRUENAS_MINI_SPECS },
    ],
  },
  // NEW: Lab Testing (DUT)
  {
    id: 'lab-testing',
    name: 'Lab Testing (DUT)',
    description: 'Hardware test lab with data acquisition, measurement automation, and device-under-test control.',
    nodeCount: 6,
    estimatedCost: 3200,
    estimatedPower: 150,
    useCases: [
      'Hardware validation',
      'Device under test (DUT)',
      'Automated test equipment',
      'Data acquisition',
      'Measurement logging',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: MINI_PC_SPECS },
      { type: 'compute', count: 1, specs: NUC_SPECS, role: 'controller' },
      { type: 'compute', count: 1, specs: MINI_PC_SPECS, role: 'worker' },
      { type: 'daq', count: 2, specs: DAQ_SPECS },
      { type: 'storage', count: 1, specs: QNAP_TS464_SPECS },
    ],
  },
  // Custom (always last)
  {
    id: 'custom',
    name: 'Custom Build',
    description: 'Start from scratch with full control. Add any components you need.',
    nodeCount: 1,
    estimatedCost: 0,
    estimatedPower: 0,
    useCases: [
      'Replicate existing setup',
      'Unique requirements',
      'Mixed hardware',
      'Incremental build',
    ],
    defaultNodes: [
      { type: 'gateway', count: 1, specs: MINI_PC_SPECS },
    ],
  },
];

export function getScenarioById(id: string): ScenarioPreset | undefined {
  return SCENARIO_PRESETS.find((s) => s.id === id);
}

// ============================================
// DEFAULT CONFIGS
// ============================================

export const DEFAULT_NETWORK_CONFIG = {
  subnet: '192.168.200.0/24',
  gateway: '192.168.200.1',
  netmask: '255.255.255.0',
  domain: 'hermit.local',
  dhcpRangeStart: '192.168.200.100',
  dhcpRangeEnd: '192.168.200.199',
  vlans: [],
  // Multi-network support
  segments: [],
  nodeAssignments: [],
  subscriptionModel: { enabled: false },
};

export const DEFAULT_SECURITY_CONFIG = {
  vpnEnabled: true,
  vpnListenPort: 51820,
  vpnSubnet: '10.200.0.0/24',
  peers: [],
  encryptStorage: false,
  auditLogging: true,
  firewallDefaultDeny: true,
};

export const DEFAULT_FORM_FACTOR: FormFactor = 'rack-18';
